import { createApp } from 'vue';
import { eventBus } from '@tools/event-bus';
import { Logger } from '@tools/logger/logger';
import {
  URL_OFFER_ACTION_REVIEW,
  URL_OFFER_ACTION_EDIT,
  URL_OFFER_SETUP,
} from '@tools/routes/routes';
import { showSubmitLoader } from '@tools/submit-loader';
import { requestsMixin } from '@tools/requests-mixin';
import { stateBarComponent } from '@organisms/state-bar';
import ModalUnpublishComponent from '@organisms/offer/ModalUnpublishComponent.vue';
import ModalDeleteComponent from '@organisms/offer/ModalDeleteComponent.vue';

/**
 * Init Offer Modal Component
 */
export const offerActions = () => {
  const elementId = '#offer-modals';
  if (!document.querySelector(elementId)) {
    return null;
  }
  if (document.querySelector('#state-bar-app') !== null) {
    stateBarComponent();
  }

  const component = {
    delimiters: [ '${', '}' ],
    components: {
      'modal-delete': ModalDeleteComponent,
      'modal-unpublish': ModalUnpublishComponent,
    },
    mixins: [ requestsMixin ],
    data() {
      return {
        loading: false,
        offerHashId: null,
        offerState: null,
        isDeleteModalOpened: false,
        isUnpublishModalOpened: false,
        isEditModalOpened: false,
        modalTitle: '',
        redirectUrl: null,
      };
    },
    mounted() {
      eventBus.addEventListener('offer-special-edit', this.handleSpecialEdit);
      eventBus.addEventListener('offer-delete', this.handleOfferDelete);
      eventBus.addEventListener('offer-review', this.handleOfferReview);
      eventBus.addEventListener('offer-unpublish', this.handleOfferUnpublish);
    },
    beforeUnmount() {
      eventBus.removeEventListener('offer-special-edit', this.handleSpecialEdit);
      eventBus.removeEventListener('offer-delete', this.handleOfferDelete);
      eventBus.removeEventListener('offer-review', this.handleOfferReview);
      eventBus.removeEventListener('offer-unpublish', this.handleOfferUnpublish);
    },
    methods: {
      handleSpecialEdit(event) {
        const offerHashId = event.detail;
        this.specialEdit(offerHashId);
      },

      handleOfferDelete(event) {
        const { offerHashId, redirectUrl } = event.detail;
        this.delete(offerHashId, redirectUrl);
      },

      handleOfferReview(event) {
        const { offerHashId, element } = event.detail;
        this.review(offerHashId, element);
      },

      handleOfferUnpublish(event) {
        const { offerHashId, offerState, redirectUrl } = event.detail;
        this.unpublish(offerHashId, offerState, redirectUrl);
      },
      /**
       * Clear data
       */
      clear() {
        this.loading = false;
        this.offerHashId = null;
        this.offerState = null;
        this.redirectUrl = null;
      },
      /**
       * This method closes the delete offer modal.
       */
      closeDeleteModal() {
        this.isDeleteModalOpened = false;
        this.clear();
      },
      /**
       * This method displays the delete offer modal.
       */
      showDeleteModal() {
        this.isDeleteModalOpened = true;
      },
      /**
       * This method displays the unpublish offer modal.
       */
      showUnpublishModal() {
        this.isUnpublishModalOpened = true;
      },
      /**
       * This method closes the unpublish offer modal.
       */
      closeUnpublishModal() {
        this.isUnpublishModalOpened = false;
        this.clear();
      },
      /**
       * This method displays the edit offer modal.
       */
      showEditModal() {
        this.isEditModalOpened = true;
      },
      /**
       * This method closes the edit offer modal.
       */
      closeEditModal() {
        this.isEditModalOpened = false;
        this.clear();
      },
      review(offerHashId, element) {
        if (!this.loading) {
          this.offerHashId = offerHashId;
          this.loading = true;

          if (element) {
            showSubmitLoader(element);
          }

          const urlOfferReview = URL_OFFER_ACTION_REVIEW.replace(
            '{id}',
            this.offerHashId,
          );

          this
            .get(urlOfferReview)
            .finally(() => {
              // Reload offer page after offer reviewing
              window.location.reload();
            });
        }
      },
      unpublish(offerHashId, offerState, redirectUrl) {
        this.offerHashId = offerHashId;
        this.offerState = offerState;

        if (redirectUrl) {
          this.redirectUrl = redirectUrl;
        }

        this.showUnpublishModal();
      },
      delete(offerHashId, redirectUrl) {
        this.offerHashId = offerHashId;

        if (redirectUrl) {
          this.redirectUrl = redirectUrl;
        }

        this.showDeleteModal();
      },
      /**
       * This is a specialEdit method because normal edition should be
       * handled in the backend.
       * This method is for every offer that has not the right state to go to setup form.
       * This is exactly why we make a first call of URL_OFFER_ACTION_EDIT to make
       * the offer re-editable before going to setup form
       *
       * @param {*} offerHashId use to call api
       */
      specialEdit(offerHashId) {
        const urlOfferEdit = URL_OFFER_ACTION_EDIT.replace(
          '{id}',
          offerHashId,
        );

        const urlOfferSetup = URL_OFFER_SETUP
          .replace('{id}', offerHashId)
          .replace('{step}', 'intro');

        this
          .get(urlOfferEdit)
          .then(() => {
            // Redirect offer page after confirm edit
            window.location.href = urlOfferSetup;
          })
          .catch((err) => {
            Logger.warn('Issue while trying to change state of offer in edit mode', err);
            window.location.reload();
          });
      },
    },
  };

  return createApp(component).mount(elementId);
};
